import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import {
  IconChevronDown,
  IconCircleCheck,
  IconEye,
  IconHistory,
  IconLoader2,
} from "@tabler/icons-react";
import { useState, type FC, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { resourceTypeIcons } from "../../app/navigation/resourceTypeIcons.js";

export type ConfigurationProposalResourceType = keyof typeof resourceTypeIcons;

export const ConfigurationProposalStatus = {
  Pending: "pending",
  /** In the open editor's unsaved draft. */
  Viewing: "viewing",
  /** In the draft the open editor is saving. */
  Applying: "applying",
  Applied: "applied",
  Outdated: "outdated",
} as const;

export type ConfigurationProposalStatus =
  (typeof ConfigurationProposalStatus)[keyof typeof ConfigurationProposalStatus];

const RESOURCE_LABELS: Record<ConfigurationProposalResourceType, string> = {
  playbook: "playbook",
  signal: "signal",
  automation: "automation",
  voiceAgent: "voice agent",
  training: "training",
};

export interface ConfigurationProposalCardProps {
  resourceType: ConfigurationProposalResourceType;
  /** Whether the proposal creates the resource or changes an existing one. */
  operation: "create" | "update";
  /** The existing or proposed resource name. */
  resourceName: string;
  /**
   * One state-neutral sentence on what the resource does or what changes.
   */
  summary: string;
  /** A few decision-relevant facts, such as a schedule or the parts that change. */
  highlights?: readonly string[];
  status: ConfigurationProposalStatus;
  /** Review for a pending proposal, Open for an applied one. */
  onAction?: () => void;
  /** Removes the outer shell when the card is rendered inside a proposal group. */
  isGrouped?: boolean;
}

const ProposalStatus: FC<{ status: ConfigurationProposalStatus }> = (props) => {
  const { status } = props;
  const iconClassName = "size-3.5 shrink-0";

  switch (status) {
    case ConfigurationProposalStatus.Pending:
      return null;
    case ConfigurationProposalStatus.Viewing:
      return (
        <span className="inline-flex items-center gap-1 text-xs text-indigo-700">
          <IconEye aria-hidden className={iconClassName} />
          Viewing
        </span>
      );
    case ConfigurationProposalStatus.Applying:
      return (
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <IconLoader2
            aria-hidden
            className={twMerge(iconClassName, "animate-spin")}
          />
          Saving
        </span>
      );
    case ConfigurationProposalStatus.Applied:
      return (
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <IconCircleCheck aria-hidden className={iconClassName} />
          Saved
        </span>
      );
    case ConfigurationProposalStatus.Outdated:
      return (
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <IconHistory aria-hidden className={iconClassName} />
          Outdated
        </span>
      );
  }
};

function buildActionLabel(
  status: ConfigurationProposalStatus,
): string | undefined {
  switch (status) {
    case ConfigurationProposalStatus.Pending:
      return "Review";
    case ConfigurationProposalStatus.Applied:
      return "Open";
    case ConfigurationProposalStatus.Viewing:
    case ConfigurationProposalStatus.Applying:
    case ConfigurationProposalStatus.Outdated:
      return undefined;
  }
}

/**
 * One configuration change the copilot proposes, as a card in the chat. The
 * whole card is the action: it opens the editor to review a pending proposal,
 * or the resource an applied one was saved to.
 */
export const ConfigurationProposalCard: FC<ConfigurationProposalCardProps> = (
  props,
) => {
  const {
    resourceType,
    operation,
    resourceName,
    summary,
    highlights = [],
    status,
    onAction,
    isGrouped = false,
  } = props;
  const ResourceIcon = resourceTypeIcons[resourceType];
  const operationLabel = `${operation === "create" ? "New" : "Update"} ${RESOURCE_LABELS[resourceType]}`;
  const actionLabel = onAction ? buildActionLabel(status) : undefined;

  return (
    <article
      aria-label={`${operationLabel}: ${resourceName}`}
      className={twMerge(
        "group w-full min-w-0 bg-white",
        isGrouped
          ? "p-3"
          : "rounded-xl border border-border-light p-3 shadow-xs",
        actionLabel &&
          "relative cursor-pointer transition-colors hover:border-border-strong/70 hover:bg-gray-50",
      )}
    >
      <div className="flex min-w-0 flex-col">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-indigo-50 text-indigo-700">
            <ResourceIcon aria-hidden className="size-4" />
          </span>
          <div className="flex min-w-0 flex-1 items-baseline justify-between gap-2">
            <p className="min-w-0 truncate text-xs font-medium text-muted-foreground">
              {operationLabel}
            </p>
            <div className="mr-1 shrink-0">
              <ProposalStatus status={status} />
            </div>
          </div>
        </div>

        <h3
          className="mt-2.5 line-clamp-2 text-[13px] leading-[18px] font-medium text-foreground"
          title={resourceName}
        >
          {resourceName}
        </h3>

        <p className="mt-0.5 line-clamp-2 text-xs leading-[18px] text-gray-600">
          {summary}
        </p>

        {highlights.length > 0 ? (
          <p className="mt-2 min-w-0 truncate text-xs text-muted-foreground">
            {highlights.map((item, index) => (
              <span key={item}>
                {index > 0 ? " · " : null}
                {item}
              </span>
            ))}
          </p>
        ) : null}

        {actionLabel ? (
          // The button's ::after covers the card, so the whole card presses it.
          <Button
            variant={ButtonVariant.Secondary}
            size={Size.SMALL}
            onClick={onAction}
            className="mt-2.5 w-full cursor-pointer justify-center text-center group-hover:bg-gray-50 after:absolute after:inset-0 after:content-[''] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            {actionLabel}
            <span className="sr-only"> {resourceName}</span>
          </Button>
        ) : null}
      </div>
    </article>
  );
};

export interface ConfigurationProposalGroupProps {
  title: string;
  children: ReactNode;
  defaultCollapsed?: boolean;
  className?: string;
}

/** Several proposals under one collapsible heading, each rendered grouped. */
export const ConfigurationProposalGroup: FC<ConfigurationProposalGroupProps> = (
  props,
) => {
  const { title, children, defaultCollapsed = false, className } = props;
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  return (
    <section
      className={twMerge(
        "overflow-hidden rounded-xl border border-border-light bg-white shadow-xs",
        className,
      )}
    >
      <div className="flex min-w-0 items-center justify-between gap-2 px-3.5 py-2.5">
        <h2 className="min-w-0 flex-1 truncate text-xs font-medium text-muted-foreground">
          {title}
        </h2>
        <IconButton
          variant={ButtonVariant.Tertiary}
          size={Size.SMALL}
          accessibleLabel={
            isCollapsed ? "Expand proposals" : "Collapse proposals"
          }
          onClick={() => setIsCollapsed((current) => !current)}
          className="-mr-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          icon={
            <IconChevronDown
              aria-hidden
              className={twMerge(
                "size-4 transition-transform duration-200 ease-out",
                isCollapsed && "-rotate-90",
              )}
            />
          }
        />
      </div>

      <div
        className={twMerge(
          "grid transition-[grid-template-rows,opacity] duration-200 ease-out",
          isCollapsed
            ? "grid-rows-[0fr] opacity-0"
            : "grid-rows-[1fr] opacity-100",
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="divide-y divide-gray-100 border-t border-gray-100">
            {children}
          </div>
        </div>
      </div>
    </section>
  );
};
